import { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Button, Chip, CircularProgress, Paper, Skeleton, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { ClassCardBox, ClassScheduleBox, CourseCard } from "../../EducatorDashboard/styles"
import courseVector from '../../../Assets/Images/courseVector.png'
import { useNavigate } from 'react-router-dom';
import { Table } from 'react-bootstrap';
import useGetClassDate from '../../../Infrastructure/CustomHooks/useGetClassDate';
import useClassCount from '../../../Infrastructure/CustomHooks/useClassCount';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { convertTimestampToDay } from '../../../Infrastructure/Utilities/convertTimestampToDay';
import useAuthentication from '../../../Infrastructure/States/onAuthStateChange';
import paths from '../../../Infrastructure/Router/paths';
import { USER_LOGS_CLIENT } from '../../../Infrastructure/Constants/logsConstant';
import { safeClientLogEvent } from '../../../Infrastructure/Utilities/safeClientLogEvent';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export const Item = ({ courseTitle, classItem, status, onClick, classDetails, courseImage = '', handleTabChange, isClassReadyToHost, showSeeClassesButton, onSeeLearners }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const {userId} = useAuthentication()
  const { learners, classCount, countLoading } = useClassCount(classDetails?.classId)
  const cohort = classDetails?.cohort
  const courseTimes = classDetails?.times
  const classUrl = classDetails?.class_url
  const courseDuration = classDetails?.duration
  const first_class = classDetails?.first_class
  const last_class = classDetails?.last_class
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false)
  const { weekday, month, year } = useGetClassDate(first_class);
  const userTimezone = dayjs.tz.guess();
  const { weekday: lastClassWeekDay, month: lastClassMonth, year: lastClassYear } = useGetClassDate(last_class);
  const statusLabels = {
    'in_progress': 'In Progress',
    'archived': 'Archived',
    'pending': 'Pending',
    'active': 'Active',
    'denied': 'Denied',
  };
  const buttonLabels = {
    'in_progress': 'Resume',
    'archived': classItem ? 'Details' : 'Course Listing Preview',
    'pending': classItem ? 'Details' : 'Course Listing Preview',
    'active': classItem ? 'Details' : 'Course Listing Preview',
    'denied': classItem ? 'Details' : 'Course Listing Preview',
  };

  const convertToLocalStartTime = (timestamp, userTimezone) => {
    const date = new Date(timestamp);
    const options = { hour12: true, hour: 'numeric', minute: 'numeric', timeZone: userTimezone };
    return date.toLocaleString('en-US', options);
  };
  const convertToLocalEndTime = (timestamp, userTimezone) => {
    const date = new Date(timestamp);
    const options = { hour12: true, hour: 'numeric', minute: 'numeric', timeZone: userTimezone };
    return date.toLocaleString('en-US', options);
  };

  const formatSchedule = () => {
    if (!courseTimes) return [];
    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
    const scheduleArray = Object.keys(courseTimes).map((day) => {
      const { start, end } = courseTimes[day];
      const dayString = convertTimestampToDay(day, userTimezone)
      return {
        day: dayString,
        startTime: convertToLocalStartTime(start, userTimezone),
        endTime: convertToLocalEndTime(end, userTimezone),
      };
    });
    scheduleArray.sort((a, b) => {
      return dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
    });
    return scheduleArray;
  };

  const timesScheduled = formatSchedule();

  const handleClick = async (courseTitle, cohort) => {
    navigate(paths.room.path + `/${classUrl}`)
    await safeClientLogEvent({
        ...USER_LOGS_CLIENT.EDUCATOR_ROOM_ENTERED,
        uid: userId || null,
        source: "item.jsx",
        emailInfo: null,
        metadata: { 
          courseTitle, 
          cohort, 
          classUrl 
        },
        error: null
      });
  };
  const viewClasses = () => {
    handleTabChange("classes")
  }
  return (
    classItem ?
      (
        <ClassCardBox>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: "column", width: '100%', mt: 1, mb: 1 }}>
            <Box sx={{ height: '90px' }}>
              <Typography sx={{ fontSize: "20px", fontWeight: "bold", textTransform: 'capitalize', wordBreak: "break-word", textAlign: 'center' }} color={'secondary'}>
                {
                  courseTitle?.length > 60
                    ? `${courseTitle.slice(0, 60)}...`
                    : courseTitle
                }
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "16px", fontWeight: "bold" }} color={'primary'}>
              Cohort: {cohort}
            </Typography>
            <Box sx={{display:"flex", justifyContent:"space-between", width: "100%", }}>
              <Typography sx={{ fontSize: "16px", fontWeight: "bold" }} color="primary">
                Learners:{" "}
                {countLoading ? (
                    <CircularProgress size={13} ml={6}/>
                ) : (
                  classCount
                )}
              </Typography>
              {countLoading ? (
                <CircularProgress size={18} />
              ) : (
                (classCount > 0) ? (
                  <Chip
                    onClick={() => onSeeLearners(learners || [])}
                    label="See Learners"
                    color="default"
                    size="small"
                  />
                ) : (
                  <Box sx={{ width: 96 }} />
                )
              )}
            </Box>
            <Typography sx={{ fontSize: "16px", fontWeight: "bold" }} color={'primary'}>
              First Class: {month}/{weekday}/{year}
            </Typography>
            <Typography sx={{ fontSize: "16px", fontWeight: "bold" }} color={'primary'}>
              Last Class: {lastClassMonth}/{lastClassWeekDay}/{lastClassYear}
            </Typography>
            <Typography sx={{ fontSize: "16px", fontWeight: "bold" }} color={'primary'}>
              {/* eslint-disable-next-line eqeqeq */}
              Course Duration: {courseDuration} {courseDuration == "1" ? "Week" : "Weeks"}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: "column", justifyContent:"space-between", height:"100%", mt: 1 }}>
            <ClassScheduleBox>
              <Typography sx={{ fontSize: "16px", fontWeight: "bold", textTransform: 'capitalize' }} color={'secondary'} mb={1}>
                Class Schedule
              </Typography>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table aria-label="class schedule table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 'bold' }}>Day</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 'bold' }}>Start Time</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 'bold' }}>End Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timesScheduled?.map((schedule, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontSize: '12px', textTransform:'capitalize' }}>{schedule.day}</TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>{schedule.startTime}</TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>{schedule.endTime}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </ClassScheduleBox>
            <Box sx={{ display: 'flex', justifyContent: 'space-between',  mt: 2 }}>
              {status !== '' && (
                <Button variant='contained' type='button' sx={{ textTransform: 'capitalize' }} onClick={onClick}>{buttonLabels[status]}</Button>
              )}
              {isClassReadyToHost ? <Button variant='contained' type='button' sx={{ textTransform: 'capitalize'}} onClick={() => handleClick(courseTitle, cohort)}>Host Room</Button> : 
                <Typography sx={{ my: 1, textAlign: "center", fontSize: "16px", fontWeight: "bold" }}>{statusLabels[status]}</Typography>
              }
            </Box>
          </Box>
        </ClassCardBox>
      ) : (
        <CourseCard position='relative'>
          <Box
            borderBottom={`3px solid ${theme.palette.primary.main}`}
            borderRadius="30px 30px 0 0"
            overflow="hidden"
            width={250}
            height={140}
            sx={{  position: 'relative' }}
          >
            {!imageLoaded && (
              <Skeleton variant="rectangular" width={250} height={140} />
            )}
            <img
              src={imageError ? courseVector : `${courseImage}?t=${Date.now()}`}
              alt="Course"
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                setImageError(true);
                setImageLoaded(true);
              }}
              style={{
                objectFit: 'cover',
                width: '100%',
                height: '100%',
                display: imageLoaded ? 'block' : 'none',
              }}
            />
          </Box>
          <Box py={2}>
            <Typography sx={{ fontSize: "18px", fontWeight: "bold", textTransform: 'capitalize', textAlign: 'center', wordBreak: "break-word" }} color={'secondary'}>
              {
                courseTitle?.length > 60
                  ? `${courseTitle.slice(0, 60)}...`
                  : courseTitle
              }
            </Typography>
          </Box>
        <Box sx={{ display: "flex", mb: 3, flexDirection: "column" }}>
          <Typography sx={{ my: 1, textAlign: "center", fontSize: "16px", fontWeight: "bold" }} color={'primary'}>{statusLabels[status]}</Typography>
          {status !== '' && (
            <Button variant='contained' type='button' sx={{ textTransform: 'capitalize', mb: 1 }} onClick={onClick}>{buttonLabels[status]}</Button>
          )}
          {showSeeClassesButton ? <Button variant='contained' type='button' sx={{ textTransform: 'capitalize' }} onClick={viewClasses}>See Classes</Button> : <Box sx={{ height: 36, width: "180px" }} />}
        </Box>
        </CourseCard>
      )

  )
}